import { useState, useEffect } from 'react'
import { Plus, X, Edit2, Save, GripVertical, Trash2 } from 'lucide-react'
import Button from './Button'
import Input from './Input'
import Textarea from './Textarea'
import {
  getCustomHabitCategories,
  createCustomHabitCategory,
  updateCustomHabitCategory,
  deleteCustomHabitCategory,
} from '../services/customHabitCategories'
import { useOrganizationId } from '../hooks/useOrganizationId'

const CustomHabitCategoryManager = () => {
  const organizationId = useOrganizationId()
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
  })
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  useEffect(() => {
    if (organizationId) {
      loadCategories()
    }
  }, [organizationId])

  const loadCategories = async () => {
    setLoading(true)
    setError(null)
    const result = await getCustomHabitCategories(organizationId)
    if (result.success) {
      setCategories(result.data)
    } else {
      setError(result.error)
    }
    setLoading(false)
  }

  const handleAdd = async () => {
    if (!formData.name.trim()) {
      setError('Category name is required')
      return
    }

    setError(null)
    const result = await createCustomHabitCategory(organizationId, formData)
    if (result.success) {
      setCategories([...categories, result.data])
      setFormData({ name: '', description: '', icon: '' })
      setShowAddForm(false)
      setSuccess('Category created successfully')
      setTimeout(() => setSuccess(null), 3000)
    } else {
      setError(result.error)
    }
  }

  const handleUpdate = async (id, updates) => {
    setError(null)
    const result = await updateCustomHabitCategory(id, updates)
    if (result.success) {
      setCategories(categories.map(cat => cat.id === id ? result.data : cat))
      setEditingId(null)
      setSuccess('Category updated successfully')
      setTimeout(() => setSuccess(null), 3000)
    } else {
      setError(result.error)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this category? It will no longer appear in new examinations, but existing data will be preserved.')) {
      return
    }

    setError(null)
    const result = await deleteCustomHabitCategory(id)
    if (result.success) {
      setCategories(categories.filter(cat => cat.id !== id))
      setSuccess('Category deleted successfully')
      setTimeout(() => setSuccess(null), 3000)
    } else {
      setError(result.error)
    }
  }

  if (loading) {
    return <div className="text-gray-600 dark:text-gray-400">Loading categories...</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Custom Habit Categories
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Create reusable habit tracking categories for your examinations (e.g., Water Intake, Meditation, etc.)
          </p>
        </div>
        {!showAddForm && (
          <Button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2"
          >
            <Plus size={18} />
            Add Category
          </Button>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 text-sm">
          {success}
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-gray-900 dark:text-white">New Category</h4>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false)
                setFormData({ name: '', description: '', icon: '' })
                setError(null)
              }}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X size={20} />
            </button>
          </div>
          <Input
            label="Category Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Water Intake, Meditation"
            required
          />
          <Textarea
            label="Description (Optional)"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description of this category"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleAdd}
              className="flex-1"
            >
              Create Category
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAddForm(false)
                setFormData({ name: '', description: '', icon: '' })
                setError(null)
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Categories List */}
      <div className="space-y-2">
        {categories.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>No custom categories yet.</p>
            <p className="text-sm mt-1">Create your first category to get started.</p>
          </div>
        ) : (
          categories.map((category) => (
            <div
              key={category.id}
              className="p-4 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 flex items-start gap-3"
            >
              <div className="flex-1">
                {editingId === category.id ? (
                  <div className="space-y-2">
                    <Input
                      label="Category Name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                    <Textarea
                      label="Description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={() => handleUpdate(category.id, formData)}
                        className="flex items-center gap-2"
                      >
                        <Save size={16} />
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setEditingId(null)
                          setFormData({ name: '', description: '', icon: '' })
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      {category.name}
                    </h4>
                    {category.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {category.description}
                      </p>
                    )}
                  </div>
                )}
              </div>
              {editingId !== category.id && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(category.id)
                      setFormData({
                        name: category.name,
                        description: category.description || '',
                        icon: category.icon || '',
                      })
                    }}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    title="Edit category"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(category.id)}
                    className="p-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                    title="Delete category"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default CustomHabitCategoryManager

